<template>
  <div class="ant-glass-background">
    <dynamic-data-table
      :can-delete="true"
      :can-edit="true"
      :is-loading="tableColumnStatus === 'loading'"
      :table-headers="columnHeaders"
      :table-records="tableSettingsColumns"
      table-title="Columns"
      @deleteItem="setupDelete"
      @editItem="setupEdit"
    >
      <template #item.is_unique="{ value, rowId, item }">
        <v-simple-checkbox :ripple="false" :value="Boolean(value)" disabled />
      </template>
      <template #item.has_relation="{ value, rowId, item }">
        <v-simple-checkbox
          :ripple="false"
          :value="hasColumnRelation(rowId)"
          disabled
        />
      </template>
      <template #item.options_value="{ value }">
        <div class="d-flex flex-column">
          <v-chip v-for="(option, index) in value" :key="index" class="my-1">
            {{ option.length < 20 ? option : `${option.substring(0, 20)}…` }}
          </v-chip>
        </div>
      </template>
      <template #item.required="{ value }">
        <v-simple-checkbox :ripple="false" :value="Boolean(value)" disabled />
      </template>
      <template #table-actions>
        <v-dialog
          key="table-column"
          v-model="columnDialog"
          max-width="500px"
          @click:outside="closeColumnDialogs"
        >
          <template #activator="{ on, attrs }">
            <v-tooltip left>
              <template #activator="{ on: onTooltip }">
                <v-icon class="ant-icon" dense v-on="{ ...on, ...onTooltip }">
                  mdi-plus
                </v-icon>
              </template>
              <span>New column</span>
            </v-tooltip>
          </template>
          <v-card>
            <v-card-title class="justify-center text-uppercase headline">
              {{ columnItem.id ? 'Edit' : 'Add' }} Column
            </v-card-title>
            <v-divider />
            <div class="px-10 pb-5">
              <ant-input label="Type">
                <template #input-field>
                  <v-select
                    v-model="columnItem.type"
                    :disabled="columnItem.id !== undefined"
                    :items="columnTypes"
                    dense
                    filled
                    hide-details
                    placeholder="type"
                    single-line
                  />
                </template>
              </ant-input>
              <div v-if="columnItem.type" class="d-flex">
                <v-switch
                  v-model="columnItem.is_unique"
                  :disabled="columnItem.id !== undefined"
                  dense
                  hide-details
                  inset
                  label="unique"
                />
                <v-spacer />
                <v-switch
                  v-model="columnItem.required"
                  dense
                  hide-details
                  inset
                  label="required"
                />
              </div>
              <div class="d-flex">
                <ant-input label="Name">
                  <template #input-field>
                    <v-text-field
                      v-model="columnItem.name"
                      :disabled="columnItem.type === 'sbscode'"
                      dense
                      filled
                      hide-details
                      placeholder="name"
                      single-line
                    />
                  </template>
                </ant-input>
                <ant-input class="ml-2" label="Order">
                  <template #input-field>
                    <v-text-field
                      v-model="columnItem.order"
                      dense
                      filled
                      hide-details
                      label="order"
                      single-line
                      type="number"
                    />
                  </template>
                </ant-input>
              </div>
              <ant-input is-optional label="Hint">
                <template #input-field>
                  <v-text-field
                    v-model="columnItem.hint"
                    dense
                    filled
                    hide-details
                    placeholder="hint"
                    single-line
                  />
                </template>
              </ant-input>
              <div
                v-if="
                  columnItem.type &&
                  columnItem.type !== 'document' &&
                  !columnItem.is_unique
                "
                class="d-flex"
              >
                <ant-input label="Default value">
                  <template #input-field>
                    <v-text-field
                      v-if="columnItem.type === ('integer' | 'float')"
                      v-model="columnItem.default_value"
                      dense
                      filled
                      hide-details
                      placeholder="default value"
                      single-line
                      type="number"
                    />
                    <v-text-field
                      v-if="columnItem.type === 'boolean'"
                      v-model="columnItem.default_value"
                      :max="1"
                      :min="0"
                      :rules="[rules.boolean]"
                      clearable
                      dense
                      filled
                      hide-details
                      placeholder="default value"
                      single-line
                      type="number"
                    />
                    <v-text-field
                      v-else
                      v-model="columnItem.default_value"
                      dense
                      filled
                      hide-details
                      placeholder="default value"
                      single-line
                      type="text"
                    />
                  </template>
                </ant-input>
                <ant-input
                  v-if="columnItem.type === 'dropdown'"
                  class="ml-2"
                  label="Options"
                >
                  <template #input-field>
                    <v-combobox
                      v-model="columnItem.options_value"
                      :disabled="columnItem.id !== undefined"
                      chips
                      deletable-chips
                      dense
                      filled
                      hide-details
                      multiple
                      placeholder="options"
                      single-line
                      small-chips
                    />
                  </template>
                </ant-input>
              </div>
            </div>

            <v-card-actions class="ant-dialog-actions-bg ant-border-top">
              <v-spacer />
              <v-btn color="error" small text @click="closeColumnDialogs">
                Cancel
              </v-btn>
              <v-btn color="primary" elevation="0" small @click="saveColumn">
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
    </dynamic-data-table>
    <delete-dialog
      :dialog="columnDeleteDialog"
      :title="`Are you sure you want to delete '${columnItem.name}' from this table`"
      @closeDialog="closeColumnDialogs"
      @deleteAction="deleteColumn"
    />
  </div>
</template>

<script>
import DynamicDataTable from '@/components/DynamicDataTable';
import { mapGetters } from 'vuex';
import DeleteDialog from '@/components/DeleteDialog';
import AntInput from '@/components/AntInput.vue';

export default {
  name: 'TableColumns',
  components: { AntInput, DeleteDialog, DynamicDataTable },
  data: () => {
    return {
      columnHeaders: [
        {
          text: 'name',
          value: 'name',
        },
        {
          text: 'type',
          value: 'type',
        },
        {
          text: 'unique',
          value: 'is_unique',
          hasSlot: true,
        },
        {
          text: 'has relation',
          value: 'has_relation',
          hasSlot: true,
        },
        {
          text: 'hint',
          value: 'hint',
        },
        {
          text: 'default_value',
          value: 'default_value',
        },
        {
          text: 'options_value',
          value: 'options_value',
          hasSlot: true,
        },
        {
          text: 'required',
          value: 'required',
          hasSlot: true,
        },
        {
          text: 'order',
          value: 'order',
        },
        { text: 'Actions', value: 'actions', align: 'right', sortable: false },
      ],
      columnTypes: [
        'text',
        'text-field',
        'integer',
        'boolean',
        'date',
        'link',
        'email',
        'dropdown',
        'document',
        'table',
        'float',
        'sbscode',
      ],
      columnItem: {
        order: '',
        name: '',
        type: '',
        default_value: '',
        options_value: '',
        hint: '',
        required: false,
      },
      columnDialog: false,
      columnDeleteDialog: false,
      rules: {
        boolean: (value) =>
          value === ('' || null) ||
          parseInt(value) === 0 ||
          parseInt(value) === 1 ||
          'Value needs to be 0 or 1',
      },
    };
  },
  computed: {
    ...mapGetters([
      'selectedProjectTable',
      'tableSettingsColumns',
      'tableColumnStatus',
      'tableSettingsRelations',
    ]),
  },
  watch: {
    'columnItem.type'(value) {
      if (value === 'sbscode') {
        this.columnItem.name = 'SBS';
      } else {
        if (this.columnItem.name === 'SBS') {
          this.columnItem.name = '';
        }
      }
    },
  },
  methods: {
    hasColumnRelation(rowId) {
      return (
        this.tableSettingsRelations.find(
          (relation) => relation?.column?.id === rowId
        ) !== undefined
      );
    },
    closeColumnDialogs() {
      this.columnItem = Object.assign(
        {},
        {
          order: '',
          name: '',
          type: '',
          default_value: '',
          options_value: '',
          hint: '',
          required: false,
        }
      );
      this.columnDialog = false;
      this.columnDeleteDialog = false;
    },
    setupEdit(column) {
      this.columnItem = Object.assign({}, column);
      this.columnDialog = true;
    },
    setupDelete(column) {
      this.columnItem = Object.assign({}, column);
      this.columnDeleteDialog = true;
    },
    deleteColumn() {
      let body = {
        project: {
          id: this.selectedProjectTable.project,
        },
        table: {
          id: this.selectedProjectTable.id,
        },
      };

      this.$store
        .dispatch('deleteColumn', { columnId: this.columnItem.id, body })
        .then(() => {
          this.closeColumnDialogs();
        });
    },
    saveColumn() {
      // update column with id
      let data = Object.assign({}, this.columnItem);
      delete data.table;
      delete data.project;

      let body = {
        table: {
          id: this.selectedProjectTable.id,
        },
        project: {
          id: this.selectedProjectTable.project,
        },
      };

      Object.keys(data).forEach((key, index) => {
        if (data[key] === '') {
          data[key] = null;
        }
        body[key] = data[key];
      });

      body.required = !!body.required;

      if (this.columnItem.id) {
        delete body.options_value;
        delete body.type;
        delete body.is_unique;

        body.required = !!body.required;

        // update column
        this.$store
          .dispatch('updateColumn', { columnId: this.columnItem.id, body })
          .then(() => {
            this.closeColumnDialogs();
          });
      } else {
        if (this.columnItem.is_unique) {
          delete body.default_value;
        }
        // create column
        this.$store.dispatch('createColumn', { body }).then(() => {
          this.closeColumnDialogs();
        });
      }
    },
  },
};
</script>

<style scoped></style>
